import React, { FC } from "react";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, List, ListItem, ListItemText, ListItemIcon, MenuItem, SelectChangeEvent, Stack, Typography, Tooltip } from "@mui/material";
import { Close, CloseOutlined, Delete, Save } from "@mui/icons-material";
import { CustomSelect } from "../CustomSelect";
import { CustomInput } from "../CustomInput";
import { Button } from "../Button";
import TextFieldsIcon from '@mui/icons-material/TextFields';
import FilterNoneIcon from '@mui/icons-material/FilterNone';
import DataObjectIcon from '@mui/icons-material/DataObject';
import { converTo } from "../../utils/convert-to"

type TextFieldFormState = {
    label: string;
    name: string;
    placeholder: string;
    value: string;
}

const TextFieldForm = () => {
    const [state, setState] = React.useState<TextFieldFormState>({
        label: "",
        name: "",
        placeholder: "",
        value: "",
    })

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState({ ...state, [event.target.name]: event.target.value })
    }

    return (
        <Box width={"100%"} display={"flex"} flexDirection={"column"} alignItems={"center"} justifyContent={"center"} mt={4} >
            <Stack direction={"row"} spacing={4} >
                <CustomInput name="label" value={state.label} onChange={onChange} label={{ text: "Field Label" }} width={200} height={40} />
                <CustomInput name="name" value={state.name} onChange={onChange} label={{ text: "Field Attribute Name" }} width={200} height={40} />
                <CustomInput name="placeholder" value={state.placeholder} onChange={onChange} label={{ text: "Field Placeholder" }} width={200} height={40} />
                <CustomInput name="value" value={state.value} onChange={onChange} label={{ text: "Field Default Value" }} width={200} height={40} />
            </Stack>
            {
                state.label && (
                    <Box width={300} borderRadius={4} p={2} border={"1px solid #1BA0E9"} mt={4}>
                        <CustomInput readOnly width={200} label={{ text: `${state.label} ${state.name ? `(attr name: ${state.name})` : ""}` }} placeholder={state.placeholder} value={state.value} />
                    </Box>
                )
            }
        </Box>
    )
}

const DropdownForm = () => {
    const [options, setOptions] = React.useState<{ id: string, description: string, value: any }[]>([]);

    const [openNewOptionForm, setOpenNewOptionForm] = React.useState(false);

    const onSave = ({ id, value, description, as } : { id: string, value: string, description: string, as: string }) => {
        setOptions([...options, { id, value: converTo(value, as), description }]);
    };

    const onToggleNewOptionForm = () => {
        setOpenNewOptionForm(!openNewOptionForm);
    }

    const onDeleteItem = (id: string) => {
        const arr = options.filter((option) => option.id !== id);
        setOptions(arr);
    }

    const DropdownItem = ({ id, description, value }: { id: string, description: string, value: any }) => {
        return (
            <Tooltip sx={{ maxWidth: 300 }} title={
                <React.Fragment>
                    <pre>
                        { JSON.stringify({ id, description, value }, null, 2) }
                    </pre>
                </React.Fragment>
            } placement="top" >
                <ListItem sx={{ my: 2, backgroundColor: "#E3EEFA", borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }} >
                    <Box display="flex" alignItems="center" gap={1} >
                        <FilterNoneIcon sx={{ color: "#1BA0E9" }} />
                        <Typography fontSize={16} fontWeight={"600"} sx={{ color: "#1BA0E9" }}>
                            {description}
                        </Typography>
                    </Box>
                    <IconButton onClick={() => onDeleteItem(id)} color="error" >
                        <Delete />
                    </IconButton>
                </ListItem>
            </Tooltip>
        )
    }

    const NewOptionForm = () => {
        const [newOptionState, setNewOptionState] = React.useState({ id: (options.length + 1).toString(), value: "", description: "", as: "" });
        const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            setNewOptionState({ ...newOptionState, [event.target.name]: event.target.value })
        }

        return (
            <ListItem sx={{ gap: 1, mt: 2 }}>
                <CustomInput name="id" value={newOptionState.id} width={100} height={35} placeholder="id" readOnly />
                <CustomInput name="description" value={newOptionState.description} onChange={onChange} width={100} height={35} placeholder="description" />
                <CustomInput name="value" value={newOptionState.value} onChange={onChange} width={100} height={35} placeholder="value" />
                <CustomSelect onChange={(event) => setNewOptionState({ ...newOptionState, as: event.target.value as string })} value={newOptionState.as} name="as" width={100} >
                    <MenuItem value="string">string</MenuItem>
                    <MenuItem value="number">integer</MenuItem>
                    <MenuItem value="boolean">boolean</MenuItem>
                </CustomSelect>
                <IconButton color="primary" onClick={() => onSave(newOptionState)} >
                    <Save />
                </IconButton>
                <IconButton color="secondary" onClick={onToggleNewOptionForm} >
                    <CloseOutlined />
                </IconButton>
            </ListItem>
        )
    }

    return (
        <Box width={"100%"} display={"flex"} alignItems={"center"} justifyContent={"center"} gap={2} mt={4} >
            <CustomInput width={250} height={40} label={{ text: "Dropdown Label" }} />
            <Box sx={{ width: "100%", maxWidth: 360, maxHeight: 300, overflow: "auto", borderRadius: 2, bgcolor: "background.paper", border: "2px solid #1BA0E9", p: 2 }} >
                <List sx={{ maxHeight: "100%" }} >
                    {
                        options.map((option) => <DropdownItem key={option.id} id={option.id} description={option.description} value={option.value} />)
                    }
                    { openNewOptionForm && <NewOptionForm /> }
                </List>
                <Button sx={{ mt: 2 }} width="100%" height={35} onClick={onToggleNewOptionForm} label="new option" />
            </Box>
        </Box>
    )
}

type EditLeadNewFieldDialogState = "T" | "D" | "N";

export const EditLeadNewFieldDialog: FC<{ isOpen: boolean; onClose: () => void }> = (props) => {
    const [state, setState] = React.useState<EditLeadNewFieldDialogState>("N");

    const onChange = (event: SelectChangeEvent<unknown>) => {
        setState(event.target.value as any);
    }

    return (
        <Dialog
            fullWidth
            maxWidth="lg"
            open={props.isOpen}
            onClose={props.onClose}
        >
            <DialogTitle sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }} >
                <Typography fontSize={22} fontWeight="600" color="#1BA0E9" textAlign="center">
                    Add a form field
                </Typography>
                <IconButton onClick={props.onClose} >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <CustomSelect onChange={onChange} placeholder="Select" StartIcon={<TextFieldsIcon />} label="What kind of field would you like to add?" >
                    <MenuItem value="T" >
                        Text Field
                    </MenuItem>
                    <MenuItem value="D" >
                        Dropdown
                    </MenuItem>
                </CustomSelect>
                {state === "T" && <TextFieldForm />}
                {state === "D" && <DropdownForm />}
                <Box display="flex" alignItems="center" justifyContent="flex-end" gap={2} sx={{ width: "100%", borderTop: "1px solid rgba(0, 0, 0, 0.12)", mt: 4, p: 2 }}>
                    <Button label="Add" />
                    <Button label="Close" variant="outlined" />
                </Box>
            </DialogContent>
        </Dialog>
    )
}